import { useState, useEffect, useMemo, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  ArrowLeft, DollarSign, TrendingUp, Clock, ArrowDownCircle,
  Receipt, Wallet, QrCode, ShieldCheck, UserCircle2, Landmark, ExternalLink
} from 'lucide-react';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { normalizeDisplayText, normalizeObjectStrings } from '../lib/utils';
import SupportWidget from '../components/SupportWidget';

const BACKEND_URL =
  window.location.hostname.endsWith('trycloudflare.com')
    ? window.location.origin
    : (process.env.REACT_APP_BACKEND_URL || window.location.origin);

const API = `${BACKEND_URL}/api`;
const WITHDRAW_LOCAL_LOCK_MS = 45000;
const WITHDRAW_LOCK_STORAGE_KEY = 'vn_withdraw_lock_v1';

const COUNTRY_CODE_OPTIONS = [
  { value: '+55', label: 'Brasil (+55)' },
  { value: '+1', label: 'EUA (+1)' },
  { value: '+351', label: 'Portugal (+351)' },
];

const DEFAULT_WALLET_IDENTITY = {
  wallet_profile_completed: false,
  whatsapp_country_code: '+55',
  whatsapp_number: '',
  bride_full_name: '',
  bride_cpf: '',
  groom_full_name: '',
  groom_cpf: '',
  payout_receiver: 'noiva',
};

const DEFAULT_PAYOUT_ACCOUNT = {
  onboarding_enabled: false,
  withdrawals_enabled: false,
  configured: false,
  ready: false,
  recipient_status: '',
  kyc_status: '',
  kyc_status_reason: '',
  requires_kyc: false,
  bank_code: '',
  bank_account_type: '',
  bank_account_last4: '',
};

const DEFAULT_PAYOUT_FORM = {
  phone: '',
  mother_name: '',
  birthdate: '',
  monthly_income: '',
  professional_occupation: '',
  address_street: '',
  address_number: '',
  address_complement: '',
  address_neighborhood: '',
  address_city: '',
  address_state: '',
  postal_code: '',
  reference_point: '',
  bank_code: '',
  branch_number: '',
  branch_check_digit: '',
  account_number: '',
  account_check_digit: '',
  account_type: 'checking',
};

const toWalletIdentityFormState = (value = {}) => {
  const normalized = normalizeObjectStrings(value || {});
  const merged = { ...DEFAULT_WALLET_IDENTITY, ...normalized };

  return {
    ...merged,
    whatsapp_number: formatPhoneInput(merged.whatsapp_number),
    bride_cpf: formatCpfInput(merged.bride_cpf),
    groom_cpf: formatCpfInput(merged.groom_cpf),
  };
};

const digitsOnly = (value = '') => String(value).replace(/\D/g, '');

const formatCpfInput = (value = '') => {
  const digits = digitsOnly(value).slice(0, 11);
  if (!digits) return '';
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `${digits.slice(0, 3)}.${digits.slice(3)}`;
  if (digits.length <= 9) return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6)}`;
  return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6, 9)}-${digits.slice(9)}`;
};

const formatPhoneInput = (value = '') => {
  const digits = digitsOnly(value).slice(0, 13);
  if (!digits) return '';
  if (digits.length <= 2) return `(${digits}`;
  if (digits.length <= 6) return `(${digits.slice(0, 2)}) ${digits.slice(2)}`;
  if (digits.length <= 10) return `(${digits.slice(0, 2)}) ${digits.slice(2, 6)}-${digits.slice(6)}`;
  return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
};

export default function FinancesPage() {
  const [loading, setLoading] = useState(true);
  const [balance, setBalance] = useState(null);
  const [payments, setPayments] = useState([]);
  const [withdrawals, setWithdrawals] = useState([]);
  const [showWithdrawModal, setShowWithdrawModal] = useState(false);
  const [withdrawAmount, setWithdrawAmount] = useState('');
  const [withdrawSourceId, setWithdrawSourceId] = useState('');
  const [pixKey, setPixKey] = useState('');
  const [pixKeyType, setPixKeyType] = useState('cpf');
  const [withdrawing, setWithdrawing] = useState(false);
  const [refreshingWithdrawalId, setRefreshingWithdrawalId] = useState(null);
  const [walletIdentity, setWalletIdentity] = useState(DEFAULT_WALLET_IDENTITY);
  const [showWalletSetupModal, setShowWalletSetupModal] = useState(false);
  const [walletSetupStep, setWalletSetupStep] = useState(1);
  const [savingWalletIdentity, setSavingWalletIdentity] = useState(false);
  const [payoutAccount, setPayoutAccount] = useState(DEFAULT_PAYOUT_ACCOUNT);
  const [payoutForm, setPayoutForm] = useState(DEFAULT_PAYOUT_FORM);
  const [showPayoutSetupModal, setShowPayoutSetupModal] = useState(false);
  const [payoutSetupStep, setPayoutSetupStep] = useState(1);
  const [savingPayoutAccount, setSavingPayoutAccount] = useState(false);
  const [creatingKycLink, setCreatingKycLink] = useState(false);
  const [refreshingPayoutAccount, setRefreshingPayoutAccount] = useState(false);
  const [withdrawLockUntil, setWithdrawLockUntil] = useState(0);
  const [withdrawLockNow, setWithdrawLockNow] = useState(Date.now());
  const { user } = useAuth();
  const recentWithdrawAttemptRef = useRef({ amount: null, at: 0 });
  const pendingWithdrawals = useMemo(
    () => withdrawals.filter((withdrawal) => ['pending', 'processing'].includes(withdrawal?.status)),
    [withdrawals]
  );
  const hasPendingWithdrawal = pendingWithdrawals.length > 0;
  const hasTemporaryWithdrawLock = withdrawLockUntil > withdrawLockNow;
  const withdrawLockSeconds = hasTemporaryWithdrawLock
    ? Math.max(1, Math.ceil((withdrawLockUntil - withdrawLockNow) / 1000))
    : 0;
  const walletReady = Boolean(walletIdentity?.wallet_profile_completed);
  const withdrawalSources = balance?.withdrawal_sources || [];
  const selectedWithdrawalSource = withdrawalSources.find((source) => source.id === withdrawSourceId);
  const usesPagarmeWithdrawals = selectedWithdrawalSource?.method === 'bank_account';
  const hasUnwithdrawableBalance = Number(balance?.available_balance || 0) >
    withdrawalSources.reduce((sum, source) => sum + Number(source.amount || 0), 0) + 0.01;
  const selectedSourceBalance = Math.min(
    Number(selectedWithdrawalSource?.amount || 0),
    Number(balance?.available_balance || 0)
  );
  const withdrawalMethodLabel = (source) => source.method === 'bank_account' ? 'Saque para conta bancária' : 'Saque via Pix';
  const pendingCardReleaseBalance = Number(balance?.pending_card_release_balance || 0);

  const activateWithdrawLock = (amount) => {
    const now = Date.now();
    const until = now + WITHDRAW_LOCAL_LOCK_MS;
    recentWithdrawAttemptRef.current = { amount, at: now };
    setWithdrawLockUntil(until);
    try {
      sessionStorage.setItem(
        WITHDRAW_LOCK_STORAGE_KEY,
        JSON.stringify({ amount, at: now, until })
      );
    } catch (error) {
      // ignore storage failures
    }
  };

  const clearWithdrawLock = () => {
    setWithdrawLockUntil(0);
    try {
      sessionStorage.removeItem(WITHDRAW_LOCK_STORAGE_KEY);
    } catch (error) {
      // ignore storage failures
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    try {
      const rawLock = sessionStorage.getItem(WITHDRAW_LOCK_STORAGE_KEY);
      if (!rawLock) return;
      const parsed = JSON.parse(rawLock);
      const until = Number(parsed?.until || 0);
      const amount = Number(parsed?.amount || 0);
      const at = Number(parsed?.at || 0);
      if (until > Date.now()) {
        setWithdrawLockUntil(until);
      }
      if (amount > 0 && at > 0) {
        recentWithdrawAttemptRef.current = { amount, at };
      }
    } catch (error) {
      // ignore storage parse issues
    }
  }, []);

  useEffect(() => {
    if (!withdrawLockUntil) return undefined;
    if (withdrawLockUntil <= Date.now()) {
      setWithdrawLockUntil(0);
      try {
        sessionStorage.removeItem(WITHDRAW_LOCK_STORAGE_KEY);
      } catch (error) {
        // ignore storage failures
      }
      return undefined;
    }
    const timer = setInterval(() => {
      setWithdrawLockNow(Date.now());
      if (Date.now() >= withdrawLockUntil) {
        setWithdrawLockUntil(0);
        try {
          sessionStorage.removeItem(WITHDRAW_LOCK_STORAGE_KEY);
        } catch (error) {
          // ignore storage failures
        }
      }
    }, 1000);
    return () => clearInterval(timer);
  }, [withdrawLockUntil]);

  const fetchData = async () => {
    try {
      const [balanceRes, paymentsRes, withdrawalsRes, pixKeyRes, walletIdentityRes, payoutAccountRes] = await Promise.all([
        axios.get(`${API}/balance`, { withCredentials: true }),
        axios.get(`${API}/payments/history`, { withCredentials: true }),
        axios.get(`${API}/withdrawals`, { withCredentials: true }),
        axios.get(`${API}/users/pix-key`, { withCredentials: true }),
        axios.get(`${API}/users/wallet-identity`, { withCredentials: true }),
        axios.get(`${API}/users/payout-account`, { withCredentials: true }),
      ]);
      const walletData = normalizeObjectStrings(walletIdentityRes.data || DEFAULT_WALLET_IDENTITY);
      setBalance(normalizeObjectStrings(balanceRes.data));
      setPayments(normalizeObjectStrings(paymentsRes.data || []));
      setWithdrawals(normalizeObjectStrings(withdrawalsRes.data || []));
      setWalletIdentity(toWalletIdentityFormState(walletData));
      setPayoutAccount({
        ...DEFAULT_PAYOUT_ACCOUNT,
        ...normalizeObjectStrings(payoutAccountRes.data || {}),
      });
      setShowWalletSetupModal(!walletData.wallet_profile_completed);
      setWalletSetupStep(1);
      if (pixKeyRes.data.pix_key) {
        setPixKey(normalizeDisplayText(pixKeyRes.data.pix_key));
        setPixKeyType(pixKeyRes.data.pix_key_type || 'cpf');
      }
    } catch (error) {
      console.error('Error fetching wallet data:', error);
    } finally {
      setLoading(false);
    }
  };

  const openWithdrawFlow = () => {
    if (!walletReady) {
      setShowWalletSetupModal(true);
      setWalletSetupStep(1);
      toast.error('Antes de resgatar, complete os dados de seguranca da carteira.');
      return;
    }
    if (hasTemporaryWithdrawLock) {
      toast.error(`Aguarde ${withdrawLockSeconds}s para evitar duplicidade no saque.`);
      return;
    }
    const preferred = withdrawalSources[0]?.id;
    if (!preferred) {
      toast.error('Não há saldo liberado para resgate.');
      return;
    }
    setWithdrawSourceId(preferred);
    setWithdrawAmount('');
    setShowWithdrawModal(true);
  };

  const saveWalletIdentity = async (e) => {
    e.preventDefault();

    const phoneDigits = digitsOnly(walletIdentity.whatsapp_number);
    const brideCpfDigits = digitsOnly(walletIdentity.bride_cpf);
    const groomCpfDigits = digitsOnly(walletIdentity.groom_cpf);

    if (walletSetupStep === 1) {
      if (!phoneDigits || phoneDigits.length < 10) {
        toast.error('Informe um número de WhatsApp válido para validação.');
        return;
      }
      setWalletSetupStep(2);
      return;
    }

    if (!walletIdentity.bride_full_name.trim() || !walletIdentity.groom_full_name.trim()) {
      toast.error('Preencha o nome completo da noiva e do noivo.');
      return;
    }

    if (brideCpfDigits.length !== 11 || groomCpfDigits.length !== 11) {
      toast.error('Informe CPF válido com 11 dígitos para os dois noivos.');
      return;
    }

    if (!['noiva', 'noivo'].includes(walletIdentity.payout_receiver)) {
      toast.error('Escolha quem receberá os valores do PIX.');
      return;
    }

    setSavingWalletIdentity(true);
    try {
      const response = await axios.put(
        `${API}/users/wallet-identity`,
        {
          whatsapp_country_code: walletIdentity.whatsapp_country_code,
          whatsapp_number: walletIdentity.whatsapp_number,
          bride_full_name: walletIdentity.bride_full_name,
          bride_cpf: walletIdentity.bride_cpf,
          groom_full_name: walletIdentity.groom_full_name,
          groom_cpf: walletIdentity.groom_cpf,
          payout_receiver: walletIdentity.payout_receiver,
        },
        { withCredentials: true }
      );

      const normalized = normalizeObjectStrings(response.data || DEFAULT_WALLET_IDENTITY);
      setWalletIdentity(toWalletIdentityFormState(normalized));
      setShowWalletSetupModal(false);
      setWalletSetupStep(1);
      toast.success('Dados da carteira salvos com sucesso.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Não foi possível salvar os dados da carteira.');
    } finally {
      setSavingWalletIdentity(false);
    }
  };

  const handleWithdraw = async (e) => {
    e.preventDefault();
    const amount = parseFloat(withdrawAmount);
    
    if (!Number.isFinite(amount) || amount < 1 || amount > selectedSourceBalance) {
      toast.error('Valor inválido');
      return;
    }

    if (usesPagarmeWithdrawals && !payoutAccount.ready) {
      if (!payoutAccount.configured) {
        setPayoutSetupStep(1);
        setShowPayoutSetupModal(true);
        toast.error('Cadastre a conta bancária do titular antes de resgatar.');
      } else {
        toast.error('A conta bancária ainda precisa ser aprovada antes do resgate.');
      }
      return;
    }
    if (usesPagarmeWithdrawals && !payoutAccount.withdrawals_enabled) {
      toast.error('Esse tipo de resgate ainda não está disponível. Nenhum valor foi movimentado.');
      return;
    }

    if (!usesPagarmeWithdrawals && !pixKey) {
      toast.error('Informe sua chave PIX');
      return;
    }

    const now = Date.now();
    if (hasTemporaryWithdrawLock) {
      toast.error(`Aguarde ${withdrawLockSeconds}s antes de tentar um novo saque.`);
      return;
    }
    const recentAttempt = recentWithdrawAttemptRef.current;
    if (
      recentAttempt.amount !== null &&
      Math.abs(Number(recentAttempt.amount) - amount) < 0.005 &&
      now - recentAttempt.at < 15000
    ) {
      toast.error('Você acabou de solicitar esse mesmo saque. Aguarde alguns segundos antes de tentar novamente.');
      return;
    }

    if (hasPendingWithdrawal) {
      const hasSameAmountPending = pendingWithdrawals.some((withdrawal) =>
        Math.abs(Number(withdrawal.amount || 0) - amount) < 0.005
      );
      if (hasSameAmountPending) {
        toast.error('Já existe um saque pendente com esse valor. Aguarde a confirmação antes de tentar novamente.');
      } else {
        toast.error('Existe um saque pendente/em processamento. Aguarde finalizar para solicitar um novo.');
      }
      return;
    }

    setWithdrawing(true);
    try {
      if (!usesPagarmeWithdrawals) {
        // Persist the account PIX key first, then request withdraw using only account-linked key.
        await axios.put(`${API}/users/pix-key`, {
          pix_key: pixKey,
          pix_key_type: pixKeyType
        }, { withCredentials: true });
      }

      activateWithdrawLock(amount);
      await axios.post(`${API}/withdraw`, {
        amount,
        withdrawal_source: withdrawSourceId,
      }, { withCredentials: true });

      toast.success(`Saque de R$ ${amount.toFixed(2)} solicitado! O valor saiu do saldo disponível e agora está pendente até confirmação do banco.`);
      setShowWithdrawModal(false);
      setWithdrawAmount('');
      fetchData();
    } catch (error) {
      const detail = String(error?.response?.data?.detail || '').toLowerCase();
      const keepLock =
        detail.includes('pendente') ||
        detail.includes('processamento') ||
        detail.includes('duplic') ||
        detail.includes('already');
      if (!keepLock) {
        clearWithdrawLock();
      }
      toast.error(error.response?.data?.detail || 'Erro ao processar saque');
    } finally {
      setWithdrawing(false);
    }
  };

  const closePayoutSetup = () => {
    if (savingPayoutAccount) return;
    setShowPayoutSetupModal(false);
    setPayoutSetupStep(1);
    setPayoutForm(DEFAULT_PAYOUT_FORM);
  };

  const updatePayoutField = (field, value) => {
    setPayoutForm((current) => ({ ...current, [field]: value }));
  };

  const savePayoutAccount = async (e) => {
    e.preventDefault();
    if (payoutSetupStep === 1) {
      if (digitsOnly(payoutForm.phone).length < 10 || !payoutForm.mother_name.trim() || !payoutForm.birthdate || !payoutForm.professional_occupation.trim() || Number(payoutForm.monthly_income) <= 0) {
        toast.error('Preencha todos os dados pessoais do titular.');
        return;
      }
      setPayoutSetupStep(2);
      return;
    }
    if (payoutSetupStep === 2) {
      const addressRequired = ['address_street', 'address_number', 'address_neighborhood', 'address_city', 'address_state', 'postal_code', 'reference_point'];
      if (addressRequired.some((field) => !String(payoutForm[field] || '').trim())) {
        toast.error('Preencha o endereço completo do titular.');
        return;
      }
      setPayoutSetupStep(3);
      return;
    }

    const bankRequired = ['bank_code', 'branch_number', 'account_number', 'account_check_digit'];
    if (bankRequired.some((field) => !String(payoutForm[field] || '').trim())) {
      toast.error('Preencha todos os dados obrigatórios da conta bancária.');
      return;
    }

    setSavingPayoutAccount(true);
    try {
      const response = await axios.put(`${API}/users/payout-account`, {
        ...payoutForm,
        monthly_income: Number(payoutForm.monthly_income),
      }, { withCredentials: true });
      setPayoutAccount({ ...DEFAULT_PAYOUT_ACCOUNT, ...normalizeObjectStrings(response.data || {}) });
      setShowPayoutSetupModal(false);
      setPayoutSetupStep(1);
      setPayoutForm(DEFAULT_PAYOUT_FORM);
      toast.success('Conta bancária enviada com segurança para análise do Pagar.me.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Não foi possível cadastrar a conta bancária.');
    } finally {
      setSavingPayoutAccount(false);
    }
  };

  const openKycLink = async () => {
    const verificationWindow = window.open('', '_blank');
    if (verificationWindow) verificationWindow.opener = null;
    setCreatingKycLink(true);
    try {
      const response = await axios.post(`${API}/users/payout-account/kyc-link`, {}, { withCredentials: true });
      if (response.data?.ready) {
        verificationWindow?.close();
        await fetchData();
        toast.success('A conta bancária já está aprovada.');
        return;
      }
      if (!response.data?.url) {
        verificationWindow?.close();
        throw new Error('O Pagar.me não retornou o link de validação.');
      }
      if (verificationWindow) {
        verificationWindow.location = response.data.url;
      } else {
        window.location.assign(response.data.url);
      }
      toast.success('Validação aberta. O link expira em 20 minutos.');
    } catch (error) {
      verificationWindow?.close();
      toast.error(error.response?.data?.detail || error.message || 'Não foi possível abrir a validação.');
    } finally {
      setCreatingKycLink(false);
    }
  };

  const refreshPayoutAccount = async () => {
    setRefreshingPayoutAccount(true);
    try {
      const response = await axios.post(`${API}/users/payout-account/refresh`, {}, { withCredentials: true });
      setPayoutAccount({ ...DEFAULT_PAYOUT_ACCOUNT, ...normalizeObjectStrings(response.data || {}) });
      toast.success('Status da conta bancária atualizado.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Não foi possível consultar o Pagar.me agora.');
    } finally {
      setRefreshingPayoutAccount(false);
    }
  };

  const handleRefreshWithdrawal = async (withdrawalId) => {
    setRefreshingWithdrawalId(withdrawalId);
    try {
      await axios.post(`${API}/withdrawals/${withdrawalId}/refresh`, {}, { withCredentials: true });
      await fetchData();
      toast.success('Status do saque atualizado.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Não foi possível atualizar o status do saque');
    } finally {
      setRefreshingWithdrawalId(null);
    }
  };

  const isPendingWithdrawalStatus = (status) => ['pending', 'processing'].includes(status);

  const getWithdrawalStatusLabel = (status) => {
    if (status === 'completed') return 'Sacado';
    if (status === 'processing') return 'Em processamento';
    if (status === 'pending') return 'Pendente';
    if (status === 'rejected') return 'Cancelado';
    return 'Falhou';
  };

  const paymentMethodLabel = (method) => {
    const labels = {
      pix: 'PIX',
      credit_card: 'Cartão',
      boleto: 'Boleto'
    };
    return labels[method] || method;
  };

  const getPaymentStatusLabel = (payment) => {
    if (payment.status === 'approved') return 'Aprovado';
    if (payment.status === 'pending' && payment.payment_method === 'credit_card') return 'Em liberação';
    if (payment.status === 'pending') return 'Pendente';
    return 'Recusado';
  };

  const formatReleaseDate = (value) => {
    if (!value) return null;
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return null;
    return parsed.toLocaleDateString('pt-BR');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F6F1EF]">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-[#E8B7B1] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-[#333333] font-medium">Carregando...</p>
        </div>
      </div>
    );
  }

  if (!user?.has_subscription) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F6F1EF] p-6">
        <div className="text-center max-w-md">
          <DollarSign className="w-16 h-16 text-[#C8A97E] mx-auto mb-6" />
          <h1 className="text-2xl font-bold text-[#1C1C1C] mb-4" style={{ fontFamily: 'Playfair Display' }}>
            Ative sua conta primeiro
          </h1>
          <p className="text-[#333333] mb-6">
            Para acessar sua carteira, você precisa ativar sua assinatura.
          </p>
          <Link to="/dashboard">
            <Button className="bg-[#E8B7B1] hover:bg-[#DFA8A1] text-white rounded-full px-8">
              Ir para o Painel
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F6F1EF]">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/80 backdrop-blur-md border-b border-slate-100">
        <div className="flex items-center justify-between h-16 px-6">
          <div className="flex items-center gap-4">
            <Link to="/dashboard" className="flex items-center gap-2 text-[#1C1C1C] hover:text-[#C8A97E]">
              <ArrowLeft className="w-5 h-5" />
              <span className="hidden sm:inline">Voltar</span>
            </Link>
            <div className="h-6 w-px bg-slate-200" />
            <h1 className="text-lg font-bold text-[#1C1C1C]" style={{ fontFamily: 'Playfair Display' }}>
              Minha carteira
            </h1>
          </div>
          <Button
            onClick={openWithdrawFlow}
            disabled={walletReady && (!balance || withdrawalSources.length === 0 || hasPendingWithdrawal || hasTemporaryWithdrawLock)}
            className="bg-[#C8A97E] hover:bg-[#B69669] text-white rounded-full px-6"
            data-testid="withdraw-btn"
          >
            <Wallet className="w-4 h-4 mr-2" />
            {!walletReady
              ? 'Concluir segurança'
              : hasPendingWithdrawal
                ? 'Saque em processamento'
                : hasTemporaryWithdrawLock
                  ? `Aguarde ${withdrawLockSeconds}s`
                  : 'Resgatar'}
          </Button>
        </div>
      </header>

      <div className="max-w-6xl mx-auto p-6">
        {/* Balance Cards */}
        <div className="grid md:grid-cols-4 gap-4 mb-8">
          <div className="card-dashboard bg-gradient-to-br from-[#FDEBE8] to-[#E8B7B1]/45 border border-[#E8B7B1]/35">
            <div className="flex items-center justify-between mb-4">
              <Wallet className="w-8 h-8 text-[#C8A97E]" />
              <TrendingUp className="w-5 h-5 text-green-400" />
            </div>
            <p className="text-sm text-[#333333] uppercase tracking-wider">
              {hasUnwithdrawableBalance ? 'Saldo na carteira' : 'Saldo disponível'}
            </p>
            <p className="text-3xl font-bold mt-1" data-testid="available-balance">
              R$ {balance?.available_balance?.toFixed(2) || '0,00'}
            </p>
            <p className="mt-2 text-xs text-[#333333]">
              {hasUnwithdrawableBalance
                ? 'Parte do saldo ainda não está disponível para resgate.'
                : 'Valor já liberado para resgate.'}
            </p>
          </div>

          <div className="card-dashboard">
            <div className="flex items-center justify-between mb-4">
              <TrendingUp className="w-8 h-8 text-green-500" />
            </div>
            <p className="text-sm text-[#333333] uppercase tracking-wider">Total Recebido</p>
            <p className="text-2xl font-bold text-[#1C1C1C] mt-1">
              R$ {balance?.total_received?.toFixed(2) || '0,00'}
            </p>
            <p className="mt-2 text-xs text-[#333333]">
              Soma de todos os pagamentos aprovados.
            </p>
          </div>

          <div className="card-dashboard">
            <div className="flex items-center justify-between mb-4">
              <Clock className="w-8 h-8 text-orange-500" />
            </div>
            <p className="text-sm text-[#333333] uppercase tracking-wider">Aguardando Confirmação</p>
            <p className="text-2xl font-bold text-[#1C1C1C] mt-1">
              R$ {balance?.pending_balance?.toFixed(2) || '0,00'}
            </p>
            <p className="mt-2 text-xs text-[#333333]">
              Inclui cartão em liberação e saques pendentes.
            </p>
          </div>

          <div className="card-dashboard">
            <div className="flex items-center justify-between mb-4">
              <ArrowDownCircle className="w-8 h-8 text-purple-500" />
            </div>
            <p className="text-sm text-[#333333] uppercase tracking-wider">Resgatado</p>
            <p className="text-2xl font-bold text-[#1C1C1C] mt-1">
              R$ {balance?.withdrawn?.toFixed(2) || '0,00'}
            </p>
            <p className="mt-2 text-xs text-[#333333]">
              Valores já concluídos pelo banco.
            </p>
          </div>
        </div>

        {pendingCardReleaseBalance > 0 && (
          <div className="card-dashboard mb-8 border border-amber-200 bg-amber-50">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center flex-shrink-0">
                <Clock className="w-6 h-6 text-amber-700" />
              </div>
              <div>
                <p className="font-bold text-amber-900">Cartão em liberação para a carteira</p>
                <p className="text-sm text-amber-800 mt-1">
                  R$ {pendingCardReleaseBalance.toFixed(2)} já foi pago no cartão, mas só entra no saldo disponível após o prazo de até 3 dias.
                </p>
              </div>
            </div>
          </div>
        )}

        {!walletReady && (
          <div className="card-dashboard mb-8 border border-[#E8B7B1] bg-[#FDEBE8]">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div>
                <p className="font-bold text-[#1C1C1C] flex items-center gap-2">
                  <ShieldCheck className="w-5 h-5 text-[#C8A97E]" />
                  Validação obrigatória da carteira
                </p>
                <p className="text-sm text-[#333333] mt-1">
                  Antes do primeiro saque, confirme WhatsApp, dados dos noivos e quem vai receber o PIX.
                </p>
              </div>
              <Button
                onClick={() => {
                  setWalletSetupStep(1);
                  setShowWalletSetupModal(true);
                }}
                className="bg-[#C8A97E] hover:bg-[#B69669] text-white rounded-full"
                data-testid="wallet-security-btn"
              >
                Completar agora
              </Button>
            </div>
          </div>
        )}

        {payoutAccount.onboarding_enabled && walletReady && (
          <div className={`card-dashboard mb-8 border ${
            payoutAccount.ready
              ? 'border-green-200 bg-green-50'
              : payoutAccount.recipient_status === 'refused'
                ? 'border-red-200 bg-red-50'
                : 'border-[#E8B7B1] bg-[#FDEBE8]'
          }`}>
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div className="flex items-start gap-3">
                <div className="w-11 h-11 rounded-xl bg-white flex items-center justify-center border border-black/5 flex-shrink-0">
                  <Landmark className="w-5 h-5 text-[#C48A83]" />
                </div>
                <div>
                  <p className="font-bold text-[#1C1C1C]">Conta bancária para resgates</p>
                  {!payoutAccount.configured && (
                    <p className="text-sm text-[#333333] mt-1">
                      Cadastre a conta do titular escolhido para preparar os resgates pelo Pagar.me.
                    </p>
                  )}
                  {payoutAccount.configured && payoutAccount.ready && (
                    <p className="text-sm text-green-800 mt-1">
                      Conta aprovada: banco {payoutAccount.bank_code}, final {payoutAccount.bank_account_last4}.
                      {!payoutAccount.withdrawals_enabled && ' Esse tipo de resgate ainda não está disponível.'}
                    </p>
                  )}
                  {payoutAccount.configured && !payoutAccount.ready && payoutAccount.recipient_status !== 'refused' && (
                    <p className="text-sm text-[#6B4B45] mt-1">
                      Cadastro recebido. Status: {payoutAccount.recipient_status === 'registration' ? 'análise inicial' : 'validação de identidade'}.
                    </p>
                  )}
                  {payoutAccount.recipient_status === 'refused' && (
                    <p className="text-sm text-red-700 mt-1">
                      O cadastro foi recusado pelo Pagar.me e precisa de atendimento antes de liberar resgates.
                    </p>
                  )}
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                {!payoutAccount.configured && (
                  <Button
                    type="button"
                    onClick={() => {
                      setPayoutSetupStep(1);
                      setShowPayoutSetupModal(true);
                    }}
                    className="bg-[#C48A83] hover:bg-[#B87A73] text-white rounded-full"
                  >
                    Cadastrar conta
                  </Button>
                )}
                {payoutAccount.requires_kyc && (
                  <Button
                    type="button"
                    onClick={openKycLink}
                    disabled={creatingKycLink}
                    className="bg-[#C48A83] hover:bg-[#B87A73] text-white rounded-full"
                  >
                    <ExternalLink className="w-4 h-4 mr-2" />
                    {creatingKycLink ? 'Abrindo...' : 'Fazer validação'}
                  </Button>
                )}
                {payoutAccount.configured && (
                  <Button type="button" variant="outline" onClick={refreshPayoutAccount} disabled={refreshingPayoutAccount} className="rounded-full">
                    {refreshingPayoutAccount ? 'Consultando...' : 'Atualizar status'}
                  </Button>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Payment Methods Info */}
        <div className="card-dashboard mb-8">
          <h3 className="font-bold text-[#1C1C1C] mb-4 flex items-center gap-2">
            <QrCode className="w-5 h-5 text-[#C8A97E]" />
            Regras de entrada na carteira
          </h3>
          <div className="grid gap-4 md:grid-cols-2">
            <div className="bg-green-50 rounded-lg p-4 border border-green-200">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-green-500 rounded-xl flex items-center justify-center flex-shrink-0">
                  <QrCode className="w-6 h-6 text-white" />
                </div>
                <div>
                  <p className="font-bold text-green-800">PIX entra mais rápido</p>
                  <p className="text-sm text-green-700 mt-1">
                    Presentes pagos via PIX entram no fluxo normal da carteira e, ao clicar em resgatar, o valor sai do saldo disponível e fica pendente apenas até a confirmação do banco.
                  </p>
                  <p className="text-xs text-green-700 mt-2">
                    Recebedor configurado:{' '}
                    <strong>
                      {walletIdentity.payout_receiver === 'noivo' ? 'Noivo' : 'Noiva'}
                    </strong>
                  </p>
                </div>
              </div>
            </div>
            <div className="bg-amber-50 rounded-lg p-4 border border-amber-200">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-amber-500 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Clock className="w-6 h-6 text-white" />
                </div>
                <div>
                  <p className="font-bold text-amber-900">Cartão libera em até 3 dias</p>
                  <p className="text-sm text-amber-800 mt-1">
                    Quando o convidado paga no cartão, o presente pode aparecer como aprovado, mas o valor fica em liberação antes de virar saldo disponível para saque.
                  </p>
                  <p className="text-xs text-amber-800 mt-2">
                    Esse valor continua protegido na carteira até o prazo operacional do cartão terminar.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Withdrawal History */}
        {withdrawals.length > 0 && (
          <div className="mb-8">
            <h2 className="text-xl font-bold text-[#1C1C1C] mb-4" style={{ fontFamily: 'Playfair Display' }}>
              Histórico de Saques
            </h2>
            <div className="card-dashboard overflow-hidden p-0">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-50 border-b border-slate-100">
                    <tr>
                      <th className="text-left px-6 py-4 text-sm font-bold text-[#1C1C1C] uppercase tracking-wider">Data</th>
                      <th className="text-left px-6 py-4 text-sm font-bold text-[#1C1C1C] uppercase tracking-wider">Destino</th>
                      <th className="text-left px-6 py-4 text-sm font-bold text-[#1C1C1C] uppercase tracking-wider">Status</th>
                      <th className="text-right px-6 py-4 text-sm font-bold text-[#1C1C1C] uppercase tracking-wider">Valor</th>
                      <th className="text-right px-6 py-4 text-sm font-bold text-[#1C1C1C] uppercase tracking-wider">Ações</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {withdrawals.map((withdrawal) => (
                      <tr key={withdrawal.withdrawal_id} className="hover:bg-slate-50">
                        <td className="px-6 py-4 text-sm text-[#333333]">
                          {new Date(withdrawal.created_at).toLocaleDateString('pt-BR')}
                        </td>
                        <td className="px-6 py-4 text-sm text-[#333333]">
                          {withdrawal.payout_destination_type === 'bank_account'
                            ? `Banco ${withdrawal.payout_destination_bank || '-'} •••• ${withdrawal.payout_destination_last4 || '-'}`
                            : withdrawal.pix_key
                              ? `${withdrawal.pix_key.substring(0, 10)}...`
                              : '-'}
                        </td>
                        <td className="px-6 py-4">
                          <span className={`px-2 py-1 rounded text-xs font-medium ${
                            withdrawal.status === 'completed' 
                              ? 'bg-green-100 text-green-700'
                              : isPendingWithdrawalStatus(withdrawal.status)
                              ? 'bg-yellow-100 text-yellow-700'
                              : 'bg-red-100 text-red-700'
                          }`}>
                            {getWithdrawalStatusLabel(withdrawal.status)}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-right text-sm font-bold text-green-600">
                          R$ {withdrawal.amount?.toFixed(2)}
                        </td>
                        <td className="px-6 py-4 text-right">
                          {isPendingWithdrawalStatus(withdrawal.status) ? (
                            <Button
                              type="button"
                              variant="outline"
                              className="rounded-full"
                              onClick={() => handleRefreshWithdrawal(withdrawal.withdrawal_id)}
                              disabled={refreshingWithdrawalId === withdrawal.withdrawal_id}
                            >
                              {refreshingWithdrawalId === withdrawal.withdrawal_id ? 'Atualizando...' : 'Atualizar'}
                            </Button>
                          ) : (
                            <span className="text-xs text-[#333333]">-</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* Payment History */}
        <div>
          <h2 className="text-xl font-bold text-[#1C1C1C] mb-4" style={{ fontFamily: 'Playfair Display' }}>
            Histórico de Pagamentos
          </h2>
          {payments.length === 0 ? (
            <div className="card-dashboard text-center py-12">
              <Receipt className="w-16 h-16 text-[#333333] mx-auto mb-4" />
              <p className="text-[#333333]">Nenhum pagamento recebido ainda</p>
              <p className="text-sm text-[#333333] mt-2">
                Quando seus convidados presentearem, os pagamentos aparecerão aqui
              </p>
            </div>
          ) : (
            <div className="card-dashboard overflow-hidden p-0">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-50 border-b border-slate-100">
                    <tr>
                      <th className="text-left px-6 py-4 text-sm font-bold text-[#1C1C1C] uppercase tracking-wider">Data</th>
                      <th className="text-left px-6 py-4 text-sm font-bold text-[#1C1C1C] uppercase tracking-wider">Presente</th>
                      <th className="text-left px-6 py-4 text-sm font-bold text-[#1C1C1C] uppercase tracking-wider">Pagador</th>
                      <th className="text-left px-6 py-4 text-sm font-bold text-[#1C1C1C] uppercase tracking-wider">Método</th>
                      <th className="text-left px-6 py-4 text-sm font-bold text-[#1C1C1C] uppercase tracking-wider">Status</th>
                      <th className="text-right px-6 py-4 text-sm font-bold text-[#1C1C1C] uppercase tracking-wider">Valor</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {payments.map((payment) => (
                      <tr key={payment.payment_id} className="hover:bg-slate-50">
                        <td className="px-6 py-4 text-sm text-[#333333]">
                          {new Date(payment.created_at).toLocaleDateString('pt-BR')}
                        </td>
                        <td className="px-6 py-4 text-sm font-medium text-[#1C1C1C]">
                          {payment.gift_name || payment.gift_id}
                        </td>
                        <td className="px-6 py-4 text-sm text-[#333333]">
                          {payment.payer_name}
                        </td>
                        <td className="px-6 py-4 text-sm text-[#333333]">
                          <span className="px-2 py-1 bg-slate-100 rounded text-xs font-medium">
                            {paymentMethodLabel(payment.payment_method)}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <span className={`px-2 py-1 rounded text-xs font-medium ${
                            payment.status === 'approved' 
                              ? 'bg-green-100 text-green-700'
                              : payment.status === 'pending'
                              ? 'bg-yellow-100 text-yellow-700'
                              : 'bg-red-100 text-red-700'
                          }`}>
                            {getPaymentStatusLabel(payment)}
                          </span>
                          {payment.payment_method === 'credit_card' && payment.status === 'pending' && (
                            <p className="mt-1 text-xs text-[#333333]">
                              Libera até {formatReleaseDate(payment.wallet_available_at) || 'em até 3 dias'}
                            </p>
                          )}
                        </td>
                        <td className="px-6 py-4 text-right text-sm font-bold text-[#C8A97E]">
                          R$ {payment.amount.toFixed(2)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>

      <Dialog open={showWalletSetupModal} onOpenChange={(open) => walletReady && setShowWalletSetupModal(open)}>
        <DialogContent className="max-w-2xl [&>button]:hidden">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-[#1C1C1C] flex items-center gap-2" style={{ fontFamily: 'Playfair Display' }}>
              <ShieldCheck className="w-6 h-6 text-[#C8A97E]" />
              Validação de segurança da carteira
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={saveWalletIdentity} className="space-y-6">
            {walletSetupStep === 1 ? (
              <div className="space-y-4">
                <p className="text-[#333333] leading-relaxed">
                  Antes do primeiro resgate, confirme um número de WhatsApp para validação da titularidade da carteira.
                </p>

                <div>
                  <Label className="text-sm font-bold text-[#1C1C1C] uppercase tracking-wider mb-2 block">
                    WhatsApp para validação
                  </Label>
                  <div className="grid sm:grid-cols-[210px,1fr] gap-3">
                    <Select
                      value={walletIdentity.whatsapp_country_code}
                      onValueChange={(value) =>
                        setWalletIdentity((prev) => ({
                          ...prev,
                          whatsapp_country_code: value,
                        }))
                      }
                    >
                      <SelectTrigger className="h-12 rounded-lg">
                        <SelectValue placeholder="Código do país" />
                      </SelectTrigger>
                      <SelectContent>
                        {COUNTRY_CODE_OPTIONS.map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>

                    <Input
                      type="tel"
                      value={walletIdentity.whatsapp_number}
                      onChange={(e) =>
                        setWalletIdentity((prev) => ({
                          ...prev,
                          whatsapp_number: formatPhoneInput(e.target.value),
                        }))
                      }
                      className="h-12 rounded-lg"
                      placeholder="(21) 96545-4596"
                    />
                  </div>
                </div>
              </div>
            ) : (
              <div className="space-y-5">
                <p className="text-[#333333] leading-relaxed">
                  Agora informe os dados dos noivos e escolha quem receberá os valores dos saques PIX desta carteira.
                </p>

                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-3 p-4 rounded-xl border border-[#E8B7B1]/40 bg-[#FDEBE8]/30">
                    <p className="font-bold text-[#1C1C1C] flex items-center gap-2">
                      <UserCircle2 className="w-5 h-5 text-[#C8A97E]" />
                      Dados da noiva
                    </p>
                    <Input
                      type="text"
                      value={walletIdentity.bride_full_name}
                      onChange={(e) =>
                        setWalletIdentity((prev) => ({
                          ...prev,
                          bride_full_name: e.target.value,
                        }))
                      }
                      className="h-11 rounded-lg"
                      placeholder="Nome completo da noiva"
                    />
                    <Input
                      type="text"
                      value={walletIdentity.bride_cpf}
                      onChange={(e) =>
                        setWalletIdentity((prev) => ({
                          ...prev,
                          bride_cpf: formatCpfInput(e.target.value),
                        }))
                      }
                      className="h-11 rounded-lg"
                      placeholder="CPF da noiva"
                    />
                  </div>

                  <div className="space-y-3 p-4 rounded-xl border border-[#E8B7B1]/40 bg-[#FDEBE8]/30">
                    <p className="font-bold text-[#1C1C1C] flex items-center gap-2">
                      <UserCircle2 className="w-5 h-5 text-[#C8A97E]" />
                      Dados do noivo
                    </p>
                    <Input
                      type="text"
                      value={walletIdentity.groom_full_name}
                      onChange={(e) =>
                        setWalletIdentity((prev) => ({
                          ...prev,
                          groom_full_name: e.target.value,
                        }))
                      }
                      className="h-11 rounded-lg"
                      placeholder="Nome completo do noivo"
                    />
                    <Input
                      type="text"
                      value={walletIdentity.groom_cpf}
                      onChange={(e) =>
                        setWalletIdentity((prev) => ({
                          ...prev,
                          groom_cpf: formatCpfInput(e.target.value),
                        }))
                      }
                      className="h-11 rounded-lg"
                      placeholder="CPF do noivo"
                    />
                  </div>
                </div>

                <div>
                  <Label className="text-sm font-bold text-[#1C1C1C] uppercase tracking-wider mb-2 block">
                    Quem recebe os saques PIX?
                  </Label>
                  <div className="grid sm:grid-cols-2 gap-3">
                    <button
                      type="button"
                      onClick={() => setWalletIdentity((prev) => ({ ...prev, payout_receiver: 'noiva' }))}
                      className={`h-12 rounded-xl border font-semibold transition ${
                        walletIdentity.payout_receiver === 'noiva'
                          ? 'border-[#E8B7B1] bg-[#FDEBE8] text-[#1C1C1C]'
                          : 'border-slate-200 text-[#333333] hover:border-[#E8B7B1]'
                      }`}
                    >
                      Noiva
                    </button>
                    <button
                      type="button"
                      onClick={() => setWalletIdentity((prev) => ({ ...prev, payout_receiver: 'noivo' }))}
                      className={`h-12 rounded-xl border font-semibold transition ${
                        walletIdentity.payout_receiver === 'noivo'
                          ? 'border-[#E8B7B1] bg-[#FDEBE8] text-[#1C1C1C]'
                          : 'border-slate-200 text-[#333333] hover:border-[#E8B7B1]'
                      }`}
                    >
                      Noivo
                    </button>
                  </div>
                </div>
              </div>
            )}

            <DialogFooter>
              {walletSetupStep === 2 && (
                <Button
                  type="button"
                  variant="outline"
                  className="rounded-full"
                  onClick={() => setWalletSetupStep(1)}
                >
                  Voltar
                </Button>
              )}
              <Button type="submit" className="bg-[#E8B7B1] hover:bg-[#DFA8A1] text-white rounded-full" disabled={savingWalletIdentity}>
                {savingWalletIdentity ? 'Salvando...' : walletSetupStep === 1 ? 'Continuar' : 'Salvar e continuar'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={showPayoutSetupModal} onOpenChange={(open) => !open && closePayoutSetup()}>
        <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-[#1C1C1C] flex items-center gap-2" style={{ fontFamily: 'Playfair Display' }}>
              <Landmark className="w-6 h-6 text-[#C48A83]" />
              Conta bancária para resgates
            </DialogTitle>
          </DialogHeader>

          <div className="grid grid-cols-3 gap-2" aria-label={`Etapa ${payoutSetupStep} de 3`}>
            {[1, 2, 3].map((step) => (
              <div key={step} className={`h-1.5 rounded-full ${step <= payoutSetupStep ? 'bg-[#C48A83]' : 'bg-slate-200'}`} />
            ))}
          </div>

          <form onSubmit={savePayoutAccount} className="space-y-5" autoComplete="off">
            {payoutSetupStep === 1 && (
              <div className="space-y-4">
                <p className="text-sm text-[#333333]">
                  O Pagar.me exige estes dados para validar o titular escolhido na carteira. Eles são enviados ao banco e não ficam salvos no Virei Noiva.
                </p>
                <div>
                  <Label>Celular do titular com DDD</Label>
                  <Input inputMode="tel" value={payoutForm.phone} onChange={(e) => updatePayoutField('phone', formatPhoneInput(e.target.value))} className="h-11 mt-2" placeholder="(21) 99999-9999" required />
                </div>
                <div>
                  <Label>Nome completo da mãe</Label>
                  <Input value={payoutForm.mother_name} onChange={(e) => updatePayoutField('mother_name', e.target.value)} className="h-11 mt-2" required />
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label>Data de nascimento</Label>
                    <Input type="date" value={payoutForm.birthdate} onChange={(e) => updatePayoutField('birthdate', e.target.value)} className="h-11 mt-2" required />
                  </div>
                  <div>
                    <Label>Renda mensal</Label>
                    <Input type="number" min="1" step="0.01" value={payoutForm.monthly_income} onChange={(e) => updatePayoutField('monthly_income', e.target.value)} className="h-11 mt-2" placeholder="Ex: 3500,00" required />
                  </div>
                </div>
                <div>
                  <Label>Profissão ou ocupação</Label>
                  <Input value={payoutForm.professional_occupation} onChange={(e) => updatePayoutField('professional_occupation', e.target.value)} className="h-11 mt-2" required />
                </div>
              </div>
            )}

            {payoutSetupStep === 2 && (
              <div className="space-y-4">
                <p className="text-sm text-[#333333]">Informe o endereço residencial do titular da conta bancária.</p>
                <div className="grid sm:grid-cols-[1fr,140px] gap-4">
                  <div>
                    <Label>Rua ou avenida</Label>
                    <Input value={payoutForm.address_street} onChange={(e) => updatePayoutField('address_street', e.target.value)} className="h-11 mt-2" required />
                  </div>
                  <div>
                    <Label>Número</Label>
                    <Input value={payoutForm.address_number} onChange={(e) => updatePayoutField('address_number', e.target.value)} className="h-11 mt-2" required />
                  </div>
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label>Bairro</Label>
                    <Input value={payoutForm.address_neighborhood} onChange={(e) => updatePayoutField('address_neighborhood', e.target.value)} className="h-11 mt-2" required />
                  </div>
                  <div>
                    <Label>CEP</Label>
                    <Input inputMode="numeric" value={payoutForm.postal_code} onChange={(e) => updatePayoutField('postal_code', e.target.value)} className="h-11 mt-2" placeholder="00000-000" required />
                  </div>
                </div>
                <div className="grid sm:grid-cols-[1fr,100px] gap-4">
                  <div>
                    <Label>Cidade</Label>
                    <Input value={payoutForm.address_city} onChange={(e) => updatePayoutField('address_city', e.target.value)} className="h-11 mt-2" required />
                  </div>
                  <div>
                    <Label>UF</Label>
                    <Input maxLength={2} value={payoutForm.address_state} onChange={(e) => updatePayoutField('address_state', e.target.value.toUpperCase())} className="h-11 mt-2" required />
                  </div>
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label>Complemento</Label>
                    <Input value={payoutForm.address_complement} onChange={(e) => updatePayoutField('address_complement', e.target.value)} className="h-11 mt-2" placeholder="Opcional" />
                  </div>
                  <div>
                    <Label>Ponto de referência</Label>
                    <Input value={payoutForm.reference_point} onChange={(e) => updatePayoutField('reference_point', e.target.value)} className="h-11 mt-2" required />
                  </div>
                </div>
              </div>
            )}

            {payoutSetupStep === 3 && (
              <div className="space-y-4">
                <div className="rounded-xl bg-[#FDEBE8]/60 border border-[#E8B7B1]/50 p-4 text-sm text-[#5E403C]">
                  A conta deve pertencer ao titular escolhido na validação da carteira. Após o envio, os dados completos não poderão ser visualizados novamente no Virei Noiva.
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label>Código do banco</Label>
                    <Input inputMode="numeric" maxLength={3} value={payoutForm.bank_code} onChange={(e) => updatePayoutField('bank_code', digitsOnly(e.target.value).slice(0, 3))} className="h-11 mt-2" placeholder="Ex: 341" required />
                  </div>
                  <div>
                    <Label>Tipo de conta</Label>
                    <Select value={payoutForm.account_type} onValueChange={(value) => updatePayoutField('account_type', value)}>
                      <SelectTrigger className="h-11 mt-2"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="checking">Conta corrente</SelectItem>
                        <SelectItem value="savings">Conta poupança</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-[1fr,100px] gap-4">
                  <div>
                    <Label>Agência</Label>
                    <Input inputMode="numeric" value={payoutForm.branch_number} onChange={(e) => updatePayoutField('branch_number', digitsOnly(e.target.value))} className="h-11 mt-2" required />
                  </div>
                  <div>
                    <Label>Dígito</Label>
                    <Input maxLength={2} value={payoutForm.branch_check_digit} onChange={(e) => updatePayoutField('branch_check_digit', e.target.value)} className="h-11 mt-2" placeholder="Opcional" />
                  </div>
                </div>
                <div className="grid grid-cols-[1fr,100px] gap-4">
                  <div>
                    <Label>Número da conta</Label>
                    <Input inputMode="numeric" value={payoutForm.account_number} onChange={(e) => updatePayoutField('account_number', digitsOnly(e.target.value))} className="h-11 mt-2" required />
                  </div>
                  <div>
                    <Label>Dígito</Label>
                    <Input maxLength={2} value={payoutForm.account_check_digit} onChange={(e) => updatePayoutField('account_check_digit', e.target.value)} className="h-11 mt-2" required />
                  </div>
                </div>
              </div>
            )}

            <DialogFooter>
              <Button type="button" variant="outline" onClick={payoutSetupStep === 1 ? closePayoutSetup : () => setPayoutSetupStep((step) => step - 1)} className="rounded-full">
                {payoutSetupStep === 1 ? 'Cancelar' : 'Voltar'}
              </Button>
              <Button type="submit" disabled={savingPayoutAccount} className="bg-[#C48A83] hover:bg-[#B87A73] text-white rounded-full">
                {savingPayoutAccount ? 'Enviando com segurança...' : payoutSetupStep < 3 ? 'Continuar' : 'Enviar para análise'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Withdraw Modal */}
      <Dialog open={showWithdrawModal} onOpenChange={setShowWithdrawModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-[#1C1C1C]" style={{ fontFamily: 'Playfair Display' }}>
              {usesPagarmeWithdrawals ? 'Resgatar para conta bancária' : 'Sacar via PIX'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleWithdraw} className="space-y-4">
            <div>
              <Label className="text-sm font-bold text-[#1C1C1C] uppercase tracking-wider mb-2 block">
                Origem do saldo
              </Label>
              {withdrawalSources.length > 1 ? (
                <Select value={withdrawSourceId} onValueChange={(value) => { setWithdrawSourceId(value); setWithdrawAmount(''); }}>
                  <SelectTrigger className="h-12 rounded-lg"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {withdrawalSources.map((source) => (
                      <SelectItem key={source.id} value={source.id}>
                        {withdrawalMethodLabel(source)}: R$ {Number(source.amount).toFixed(2)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <p className="text-sm text-[#333333]">{selectedWithdrawalSource && withdrawalMethodLabel(selectedWithdrawalSource)}</p>
              )}
            </div>
            {hasTemporaryWithdrawLock && !hasPendingWithdrawal && (
              <div className="bg-amber-50 rounded-lg p-3 border border-amber-200 text-sm text-amber-800">
                Para evitar saque duplicado, aguarde {withdrawLockSeconds}s antes de tentar novamente.
              </div>
            )}
            {hasPendingWithdrawal && (
              <div className="bg-yellow-50 rounded-lg p-3 border border-yellow-200 text-sm text-yellow-800">
                Já existe saque pendente/em processamento. Para evitar duplicidade, um novo saque só pode ser solicitado após a confirmação do anterior.
              </div>
            )}
            <div>
              <p className="text-sm text-[#333333] mb-4">
                Saldo nesta origem: <span className="font-bold text-[#1C1C1C]">R$ {selectedSourceBalance.toFixed(2)}</span>
              </p>
              <Label className="text-sm font-bold text-[#1C1C1C] uppercase tracking-wider mb-2 block">
                Valor para Saque
              </Label>
              <Input
                type="number"
                step="0.01"
                min="1"
                max={selectedSourceBalance}
                value={withdrawAmount}
                onChange={(e) => setWithdrawAmount(e.target.value)}
                placeholder="0.00"
                className="h-12 rounded-lg"
                required
                data-testid="withdraw-amount-input"
              />
            </div>
            
            {!usesPagarmeWithdrawals && (
              <>
                <div>
                  <Label className="text-sm font-bold text-[#1C1C1C] uppercase tracking-wider mb-2 block">
                    Tipo de Chave PIX
                  </Label>
                  <Select value={pixKeyType} onValueChange={setPixKeyType}>
                    <SelectTrigger className="h-12 rounded-lg" data-testid="pix-key-type-select">
                      <SelectValue placeholder="Selecione o tipo" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="cpf">CPF</SelectItem>
                      <SelectItem value="cnpj">CNPJ</SelectItem>
                      <SelectItem value="email">E-mail</SelectItem>
                      <SelectItem value="phone">Telefone</SelectItem>
                      <SelectItem value="random">Chave Aleatória</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label className="text-sm font-bold text-[#1C1C1C] uppercase tracking-wider mb-2 block">
                    Sua Chave PIX
                  </Label>
                  <Input
                    type="text"
                    value={pixKey}
                    onChange={(e) => setPixKey(e.target.value)}
                    placeholder={pixKeyType === 'cpf' ? '000.000.000-00' : pixKeyType === 'email' ? 'seu@email.com' : pixKeyType === 'phone' ? '11999999999' : 'Sua chave PIX'}
                    className="h-12 rounded-lg"
                    required
                    data-testid="pix-key-input"
                  />
                </div>
              </>
            )}

            <div className="bg-green-50 rounded-lg p-4 text-sm border border-green-200">
              <div className="flex items-center gap-2 mb-2">
                <QrCode className="w-5 h-5 text-green-600" />
                <p className="font-medium text-green-800">
                  {usesPagarmeWithdrawals ? 'Resgate bancário' : 'Saque via PIX'}
                </p>
              </div>
              <ul className="space-y-1 text-green-700">
                <li>
                  {usesPagarmeWithdrawals
                    ? `Transferência para banco ${payoutAccount.bank_code}, conta final ${payoutAccount.bank_account_last4}`
                    : 'Transferência para a chave PIX cadastrada na conta'}
                </li>
                <li>Sai de disponível e entra em pendente imediatamente</li>
                <li>Só marca como sacado após confirmação do banco/provedor</li>
              </ul>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowWithdrawModal(false)} className="rounded-full">
                Cancelar
              </Button>
              <Button 
                type="submit" 
                disabled={withdrawing || hasPendingWithdrawal || hasTemporaryWithdrawLock}
                className="bg-green-600 hover:bg-green-700 text-white rounded-full"
                data-testid="confirm-withdraw-btn"
              >
                {withdrawing
                  ? 'Processando...'
                  : hasPendingWithdrawal
                    ? 'Aguardando saque anterior'
                    : hasTemporaryWithdrawLock
                      ? `Aguarde ${withdrawLockSeconds}s`
                      : usesPagarmeWithdrawals ? 'Solicitar resgate' : 'Sacar via PIX'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <SupportWidget />
    </div>
  );
}

